import { Button } from "@crew/ui/components/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorHint } from "../../app/api";
import { meKey, type Me } from "../../app/me";
import { AuthCard } from "./auth-card";
import { SignedInAs } from "./logout-controls";

/** The 6 digits a typed or pasted code means, spaces and dashes dropped; null until it's 6. */
function codeOf(value: string): string | null {
  const digits = value.replace(/[\s-]/g, "");
  return /^\d{6}$/.test(digits) ? digits : null;
}

/** Sends a new code; the old one stops working. */
function useResend() {
  return useMutation({ mutationFn: () => api("POST", "/api/auth/verify/resend") });
}

/** Checks the code; once it's right, who's signed in is read again and checkout follows. */
function useVerify() {
  const navigate = useNavigate();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api("POST", "/api/auth/verify", { code }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: meKey });
      navigate("/checkout", { replace: true });
    },
  });
}

/** "Didn't get it? Send a new code", and what came of the last one. */
function Resend({ resend, onSent }: { resend: ReturnType<typeof useResend>; onSent: () => void }) {
  return (
    <p className="text-sm text-muted-foreground" aria-live="polite">
      {resend.isSuccess ? "Sent a new code. " : "Didn't get it? "}
      <Button
        type="button"
        variant="link"
        className="h-auto p-0"
        disabled={resend.isPending}
        onClick={() => resend.mutate(undefined, { onSuccess: onSent })}
      >
        {resend.isPending ? "Sending…" : "Send a new code"}
      </Button>
      {resend.error ? (
        <span role="alert" className="mt-1 block text-destructive">
          {errorHint(resend.error)}
        </span>
      ) : null}
    </p>
  );
}

/** The code's field: why the last one was refused, or where else to look for it. */
function CodeField({
  value,
  error,
  onChange,
}: {
  value: string;
  error: unknown;
  onChange: (value: string) => void;
}) {
  const invalid = error ? true : undefined;
  return (
    <Field data-invalid={invalid}>
      <FieldLabel htmlFor="code">Code</FieldLabel>
      <Input
        id="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        required
        maxLength={7}
        className="text-center font-mono text-lg tracking-[0.3em]"
        aria-invalid={invalid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? (
        <FieldError>{errorHint(error)}</FieldError>
      ) : (
        <FieldDescription>Not in your inbox? Check spam.</FieldDescription>
      )}
    </Field>
  );
}

/**
 * Right after signup (#384): the 6-digit code emailed to the login address.
 * Checkout waits on it. The code goes in by itself once it's 6 digits, typed
 * or pasted, or with Confirm.
 */
export function VerifyPage({ me }: { me: Me }) {
  const [value, setValue] = useState("");
  const verify = useVerify();
  const resend = useResend();
  const code = codeOf(value);
  const change = (next: string) => {
    setValue(next);
    const full = codeOf(next);
    if (full && full !== code && !verify.isPending) verify.mutate(full);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code) verify.mutate(code);
  };
  const sent = () => {
    setValue("");
    verify.reset();
  };

  return (
    <AuthCard
      title="Check your email"
      description={`We sent a 6-digit code to ${me.user.email}. It works for 15 minutes.`}
      footer={<SignedInAs email={me.user.email} />}
    >
      <form onSubmit={submit}>
        <FieldGroup>
          <CodeField value={value} error={verify.error} onChange={change} />
          <Button type="submit" disabled={!code || verify.isPending}>
            {verify.isPending ? "Checking…" : "Confirm email"}
          </Button>
          <Resend resend={resend} onSent={sent} />
        </FieldGroup>
      </form>
    </AuthCard>
  );
}
